import React, { useState, useEffect } from "react";
import Api from "../../components/api/Api";
import { Col, Container, Row } from "react-bootstrap";
import { Formik, Form, Field, ErrorMessage } from "formik";
import Notification, { showToast } from "../../../components/common/notification/Notification";
import { useNavigate } from "react-router-dom";
import Spinner from "../../../components/common/spinner/Spinner";
import SingleDetailHeader from "../../components/singleDetail/SingleDetailHeader";
import BreadCrumb from "../../components/breadcrumb/BreadCrumb";

const AddPartnerLead = () => {
  const navigate = useNavigate();
  const [partners, setPartners] = useState([]);

  const addLeadApi = Api({
    url: `admin/service/lead/add`,
  });

  const getPartnersApi = Api({
    url: "admin/associate/partners",
  });

  useEffect(() => {
    const fetchPartners = async () => {
      try {
        const payload = {
          page_number: 1,
          page_size: 100, // Fetch up to 100 partners for the dropdown
          order_by: "ASC",
          keyword_search: ""
        };
        const responseData = await getPartnersApi.postData(payload);
        let fetchedData = [];
        if (responseData && responseData.data) {
          if (responseData.data.associatedPartners) {
            fetchedData = responseData.data.associatedPartners;
          } else if (responseData.data.associatedPartner) {
            fetchedData = Array.isArray(responseData.data.associatedPartner) ? responseData.data.associatedPartner : [responseData.data.associatedPartner];
          }
        }
        setPartners(fetchedData.filter(partner => partner.active)); // Only active partners
      } catch (error) {
        console.error("Error fetching partners:", error);
      }
    };
    fetchPartners();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initialValues = {
    first_name: "",
    last_name: "",
    mobile_number: "",
    email: "",
    city: "",
    state: "",
    partnerId: "",
    status: "PENDING",
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setSubmitting(true);

      const data = {
        first_name: values.first_name,
        last_name: values.last_name,
        mobile_number: values.mobile_number,
        email: values.email,
        city: values.city,
        state: values.state,
        associated_partner: {
          id: Number(values.partnerId)
        },
        status: values.status
      };

      const response = await addLeadApi.postData(data);
      if (response && response.data && response.data.statusType === "SUCCESS") {
        showToast("Partner Lead added successfully", { type: "success" });
        setTimeout(() => {
          navigate("/partner-leads");
        }, 3000);
      } else {
        showToast(response?.data?.text || "Failed to add lead", { type: "error" });
      }
    } catch (error) {
      console.error(error);
      showToast("Error Occurred", { type: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  const breadcrumbItems = [
    { label: "", link: "/admin", icon: "fa-solid fa-house" },
    { label: "Partner Network", link: "/partners" },
    { label: "Partner Leads", link: "/partner-leads" },
    { label: "Add Partner Lead", link: "", active: true },
  ];

  return (
    <>
      <Notification />
      <Container fluid style={{ marginTop: "2rem", padding: "0rem 4rem" }}>
        <BreadCrumb items={breadcrumbItems} />
        <Row>
          <Col lg={12} md={12} sm={12}>
            <SingleDetailHeader
              header="Add Partner Lead"
              description="Add a new partner lead here."
            />
            <Formik initialValues={initialValues} onSubmit={handleSubmit}>
              {({ isSubmitting }) => (
                <Form>
                  <Row>
                    <Col md={6}>
                      <div className="mb-3">
                        <label htmlFor="first_name" className="form-label">First Name</label>
                        <Field type="text" className="form-control" id="first_name" placeholder="Enter first name" name="first_name" required />
                        <ErrorMessage name="first_name" component="div" className="text-danger" />
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-3">
                        <label htmlFor="last_name" className="form-label">Last Name</label>
                        <Field type="text" className="form-control" id="last_name" placeholder="Enter last name" name="last_name" required />
                        <ErrorMessage name="last_name" component="div" className="text-danger" />
                      </div>
                    </Col>
                  </Row>
                  
                  <Row>
                    <Col md={6}>
                      <div className="mb-3">
                        <label htmlFor="mobile_number" className="form-label">Mobile Number</label>
                        <Field type="text" className="form-control" id="mobile_number" placeholder="Enter mobile number" name="mobile_number" required />
                        <ErrorMessage name="mobile_number" component="div" className="text-danger" />
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-3">
                        <label htmlFor="email" className="form-label">Email Address</label>
                        <Field type="email" className="form-control" id="email" placeholder="Enter email address" name="email" required />
                        <ErrorMessage name="email" component="div" className="text-danger" />
                      </div>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <div className="mb-3">
                        <label htmlFor="city" className="form-label">City</label>
                        <Field type="text" className="form-control" id="city" placeholder="Enter city" name="city" required />
                        <ErrorMessage name="city" component="div" className="text-danger" />
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-3">
                        <label htmlFor="state" className="form-label">State</label>
                        <Field type="text" className="form-control" id="state" placeholder="Enter state" name="state" required />
                        <ErrorMessage name="state" component="div" className="text-danger" />
                      </div>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <div className="mb-3">
                        <label htmlFor="partnerId" className="form-label">Associated Partner</label>
                        <Field as="select" className="form-control form-select" id="partnerId" name="partnerId" required>
                          <option value="">Select Partner</option>
                          {partners.map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.name}
                            </option>
                          ))}
                        </Field>
                        <ErrorMessage name="partnerId" component="div" className="text-danger" />
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-3">
                        <label htmlFor="status" className="form-label">Status</label>
                        <Field as="select" className="form-control form-select" id="status" name="status" required>
                          <option value="PENDING">Pending</option>
                          <option value="INPROGRESS">In Progress</option>
                          <option value="COMPLETED">Completed</option>
                          <option value="REJECTED">Rejected</option>
                        </Field>
                        <ErrorMessage name="status" component="div" className="text-danger" />
                      </div>
                    </Col>
                  </Row>

                  <button type="submit" className="btn btn-primary mt-4" style={{ marginBottom: "10rem" }} disabled={isSubmitting}>
                    {isSubmitting ? <Spinner loading={true} /> : "Add Partner Lead"}
                  </button>
                </Form>
              )}
            </Formik>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default AddPartnerLead;
